import { z } from 'zod';
import { baseEntityFields, idSchema, instanceIdSchema, isoDateTimeSchema, } from './common.schema';
import { ALERT_SEVERITIES, ALERT_STATUSES, ALERT_RESOLVE_REASONS, ALERT_SEVERITY_ORDER, } from '../constants/severities';
import { thresholdSchema, sensorKeySchema, datapointNameSchema, } from './sensor.schema';
/**
 * Alert — a state document, never time-series (tech-arch §4.2). Occurrence
 * dedup mirrors Desk's Alerts Hub occurrence model; delivery and escalation
 * state live on the doc so failed deliveries can retry with backoff and
 * surface as platform alerts (tech-arch §5.3).
 */
export const DELIVERY_CHANNEL_KINDS = [
    'desk',
    'dispatch',
    'webhook',
    'email',
    'push',
];
export const DELIVERY_STATUSES = ['pending', 'sent', 'failed'];
export const DELIVERY_EVENT_KINDS = [
    'raised',
    'escalated',
    'resolved',
    'acknowledged',
];
export const alertDeliverySchema = z
    .object({
    /** Stable slot identity. Delivery jobs address slots by this id —
     *  NEVER by array index, which shifts when the cap-pruner compacts
     *  terminal slots. */
    slotId: z.string().min(8).max(64),
    channel: z.enum(DELIVERY_CHANNEL_KINDS),
    /** Lifecycle event this slot delivers — preserved on the slot so
     *  recovery sweeps replay the CORRECT notification content. */
    eventKind: z.enum(DELIVERY_EVENT_KINDS),
    integrationId: idSchema.nullable(),
    /** Recipient address for email/push channels (non-empty when set). */
    target: z.string().min(1).max(320).nullable(),
    status: z.enum(DELIVERY_STATUSES),
    attempts: z.number().int().nonnegative(),
    lastAttemptAt: isoDateTimeSchema.nullable(),
    nextAttemptAt: isoDateTimeSchema.nullable(),
    /** External correlation id (Desk alert id, Dispatch request id,
     *  webhook response reference). */
    externalRef: z.string().max(512).nullable(),
    lastError: z.string().max(2000).nullable(),
    /** When the slot was reserved (#140). The stale-pending reconciler keys a never-attempted slot's recovery on THIS
     *  age, not the alert's `updatedAt` (which every new occurrence bumps). Absent on slots reserved before #140. */
    reservedAt: isoDateTimeSchema.optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    // Status-specific retry-state machine — the backoff worker must be
    // able to distinguish queued / retriable / terminal unambiguously:
    //   pending: never attempted (attempts=0, no attempt timestamps/error)
    //   sent:    attempted successfully (lastAttemptAt set, no retry, no error)
    //   failed:  attempted unsuccessfully (lastAttemptAt + lastError set;
    //            nextAttemptAt set = retriable, null = terminal)
    if (val.status === 'pending') {
        if (val.attempts !== 0 ||
            val.lastAttemptAt !== null ||
            val.nextAttemptAt !== null ||
            val.lastError !== null ||
            val.externalRef !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['status'],
                message: 'pending deliveries must have no attempt state',
            });
        }
    }
    if (val.status === 'sent') {
        if (val.lastAttemptAt === null || val.attempts < 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastAttemptAt'],
                message: 'sent deliveries require lastAttemptAt and attempts >= 1',
            });
        }
        if (val.nextAttemptAt !== null || val.lastError !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['nextAttemptAt'],
                message: 'sent deliveries must not carry retry state or an error',
            });
        }
    }
    if (val.status === 'failed') {
        if (val.lastAttemptAt === null || val.lastError === null || val.attempts < 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastError'],
                message: 'failed deliveries require lastAttemptAt, lastError, and attempts >= 1',
            });
        }
    }
    // Desk and Dispatch sends always yield an external correlation id
    // (Desk alert id / Dispatch request id) — a sent record without one
    // is unauditable. Webhooks are exempt: arbitrary receivers may not
    // return a usable reference.
    if (val.status === 'sent' &&
        (val.channel === 'desk' || val.channel === 'dispatch') &&
        val.externalRef === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['externalRef'],
            message: `sent ${val.channel} deliveries must record the external reference`,
        });
    }
    // Channel-specific routing state must be structurally complete —
    // a persisted delivery that a worker can't route is a defect.
    const integrationBacked = val.channel === 'desk' || val.channel === 'dispatch' || val.channel === 'webhook';
    if (integrationBacked) {
        if (val.integrationId === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['integrationId'],
                message: `integrationId is required for channel "${val.channel}"`,
            });
        }
        if (val.target !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['target'],
                message: `target is not allowed for channel "${val.channel}"`,
            });
        }
    }
    else {
        // email / push deliver to a target, not an integration.
        if (val.target === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['target'],
                message: `target is required for channel "${val.channel}"`,
            });
        }
        else if (val.channel === 'email' &&
            !z.string().email().safeParse(val.target).success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['target'],
                message: 'email deliveries require a valid email address target',
            });
        }
        if (val.integrationId !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['integrationId'],
                message: `integrationId is not allowed for channel "${val.channel}"`,
            });
        }
    }
});
export const alertEscalationStateSchema = z
    .object({
    chainId: idSchema.nullable(),
    ruleId: idSchema.nullable(),
    /** Index of the stage delivered most recently; -1 before first stage.
     *  Bounded by the 10-stage chain cap (escalationChainSchema), plus one: a `change-unrecovered` alert's default route
     *  puts the change owner's stage in front of a full chain (#26). */
    stageIndex: z.number().int().min(-1).max(10),
    /** `change-unrecovered` default route (#26): the user who owns the change, decided ONCE when the alert is routed. Non-null
     *  means stage 0 is the owner's stage and the chain's own stages follow, so a stage's index never shifts if the owner
     *  later leaves the tenant (the stage is then skipped, not removed). Null on every other alert. */
    ownerUserId: idSchema.nullable().default(null),
    repeatCycle: z.number().int().nonnegative(),
    nextEscalationAt: isoDateTimeSchema.nullable(),
    /** Rule flags SNAPSHOTTED at routing time: rule edits/deletes after
     *  the alert fired must not retroactively drop its lifecycle
     *  notifications (the audience is likewise slot-snapshotted). */
    sendResolved: z.boolean().default(false),
    sendAcknowledged: z.boolean().default(false),
})
    .strict()
    .superRefine((val, ctx) => {
    // Unrouted (no chain) means NO escalation progress of any kind;
    // routed state requires the rule that routed it.
    if (val.chainId === null) {
        if (val.ruleId !== null ||
            val.stageIndex !== -1 ||
            val.repeatCycle !== 0 ||
            val.nextEscalationAt !== null ||
            val.ownerUserId !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['chainId'],
                message: 'unrouted escalation state must carry no progress fields',
            });
        }
    }
    else if (val.ruleId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ruleId'],
            message: 'a routed alert must reference the rule that routed it',
        });
    }
});
export const alertNoteSchema = z
    .object({
    userId: idSchema,
    note: z.string().min(1).max(10000),
    createdAt: isoDateTimeSchema,
})
    .strict();
/** Snapshot of the threshold that fired — same shape AND same invariants
 *  as the write-time definition (at least one severity set). */
export const thresholdSnapshotSchema = thresholdSchema;
/**
 * What KIND of event an alert is (change-participation.md §5): not why it was
 * quiet (that is `suppressedBy…`) but what Detect observed. Set once, at raise
 * time, by whichever path raises the alert; immutable thereafter.
 *  - outage: unplanned (the default, and every alert stored before #26)
 *  - change-expected: raised inside a change window, on its expected impact
 *  - change-escaped: raised on a WITNESS during a change window — blast radius
 *  - change-unrecovered: raised by verification at the end of a window — the
 *    change did not complete cleanly (a distinct alert, not un-suppression)
 */
export const DETECTION_MODES = ['outage', 'change-expected', 'change-escaped', 'change-unrecovered'];
/** The change-window occurrence that classified an alert. */
export const alertChangeRefSchema = z
    .object({ changeWindowId: idSchema, occurrenceStartsAt: isoDateTimeSchema })
    .strict();
export const alertSchema = z
    .object({
    /**
     * Dedup identity: `{tenantId}#{resourceId}#{instanceId|-}#{sensorKey}#{datapoint}`.
     * A firing condition with the same dedup key increments
     * `occurrenceCount` on the existing open alert instead of creating a
     * new one (flap suppression / occurrence model).
     */
    dedupKey: z.string().min(1).max(1024),
    severity: z.enum(ALERT_SEVERITIES),
    status: z.enum(ALERT_STATUSES),
    resourceId: idSchema,
    resourceName: z.string().max(253),
    clientId: idSchema.nullable(),
    instanceId: instanceIdSchema.nullable(),
    instanceName: z.string().max(253).nullable(),
    sensorId: idSchema,
    sensorKey: sensorKeySchema,
    datapoint: datapointNameSchema,
    /** Value that triggered (or most recently re-triggered) the alert. */
    lastValue: z.number().finite().nullable(),
    thresholdSnapshot: thresholdSnapshotSchema.nullable(),
    /** True when the condition is a no-data condition, not a threshold. */
    isNoData: z.boolean(),
    startedAt: isoDateTimeSchema,
    lastOccurredAt: isoDateTimeSchema,
    occurrenceCount: z.number().int().min(1),
    /** Highest severity seen during this alert's lifetime. */
    peakSeverity: z.enum(ALERT_SEVERITIES),
    acknowledgedAt: isoDateTimeSchema.nullable(),
    acknowledgedBy: idSchema.nullable(),
    resolvedAt: isoDateTimeSchema.nullable(),
    resolveReason: z.enum(ALERT_RESOLVE_REASONS).nullable(),
    /** SDT / dependency suppression flag — suppressed alerts exist but are
     *  short-circuited at routing, never at evaluation. */
    suppressedBySdtId: idSchema.nullable(),
    /** Silent-mode suppression (#15): the migration scope that holds delivery
     *  while this tenant's monitoring is compared with the incumbent. Mirrors
     *  SDT semantics — the alert exists with full truth, only routing is held,
     *  and it PROMOTES to open when the scope goes live. */
    suppressedByScopeId: idSchema.nullable().default(null),
    detectionMode: z.enum(DETECTION_MODES).default('outage'),
    changeRef: alertChangeRefSchema.nullable().default(null),
    /** #26 (PO, 2026-10-11): a held `change-expected` alert whose condition the window's verification found unrecovered is
     *  FOLDED into the `change-unrecovered` alert for the same condition (this is that alert's id): it stays held and is
     *  never routed on its own, so a planned impact does not page on-call separately. It is released (routed as normal)
     *  only if the alert it was folded into is no longer live while the condition still fires (metric truth: a firing
     *  condition is never silently dropped). */
    foldedIntoAlertId: idSchema.nullable().default(null),
    notes: z.array(alertNoteSchema).max(200),
    escalation: alertEscalationStateSchema,
    /** Cap sized for worst-case fanout (10 stages × 50 recipients ×
     *  repeat cycles); the writer prunes oldest TERMINAL entries before
     *  the cap can block a live delivery. */
    deliveries: z.array(alertDeliverySchema).max(1000),
    /** External correlation (Desk Alerts Hub / Dispatch Request). */
    deskAlertId: z.string().max(256).nullable(),
    deskTicketId: z.string().max(256).nullable(),
    dispatchRequestId: z.string().max(256).nullable(),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => {
    // dedupKey must agree with the document's own identity fields —
    // a drifted key silently bypasses occurrence dedup and correlation.
    const expectedDedupKey = [
        val.tenantId,
        val.resourceId,
        val.instanceId ?? '-',
        val.sensorKey,
        val.datapoint,
    ].join('#');
    if (val.dedupKey !== expectedDedupKey) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dedupKey'],
            message: `dedupKey must equal "${expectedDedupKey}" derived from the alert's identity fields`,
        });
    }
    // No-data alerts have no triggering value or threshold; threshold
    // alerts must snapshot the threshold that fired.
    if (val.isNoData) {
        if (val.thresholdSnapshot !== null || val.lastValue !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['thresholdSnapshot'],
                message: 'no-data alerts must not carry a threshold snapshot or lastValue',
            });
        }
    }
    else {
        if (val.thresholdSnapshot === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['thresholdSnapshot'],
                message: 'threshold alerts must snapshot the threshold that fired',
            });
        }
        if (val.lastValue === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastValue'],
                message: 'threshold alerts must carry the triggering value',
            });
        }
    }
    // Lifecycle-state consistency — impossible combinations must not
    // validate: they would leak into routing, UI, and audit logic.
    if (ALERT_SEVERITY_ORDER[val.peakSeverity] < ALERT_SEVERITY_ORDER[val.severity]) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['peakSeverity'],
            message: 'peakSeverity cannot be lower than the current severity',
        });
    }
    if (val.status === 'resolved') {
        if (val.resolvedAt === null || val.resolveReason === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['resolvedAt'],
                message: 'resolved alerts require resolvedAt and resolveReason',
            });
        }
    }
    else if (val.resolvedAt !== null || val.resolveReason !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['resolvedAt'],
            message: 'resolution fields are only allowed when status is "resolved"',
        });
    }
    const ackAtSet = val.acknowledgedAt !== null;
    const ackBySet = val.acknowledgedBy !== null;
    if (ackAtSet !== ackBySet) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['acknowledgedBy'],
            message: 'acknowledgedAt and acknowledgedBy must be set together',
        });
    }
    if (val.status === 'acknowledged' && !ackAtSet) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['acknowledgedAt'],
            message: 'acknowledged alerts require acknowledgedAt/acknowledgedBy',
        });
    }
    // Ack metadata is history that survives into resolved/suppressed, but
    // an *open* alert with ack metadata is a contradiction.
    if (val.status === 'open' && ackAtSet) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['acknowledgedAt'],
            message: 'open alerts must not carry acknowledgement metadata',
        });
    }
    const suppressors = (val.suppressedBySdtId !== null ? 1 : 0) + (val.suppressedByScopeId !== null ? 1 : 0);
    if ((val.detectionMode === 'outage') !== (val.changeRef === null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['changeRef'],
            message: 'changeRef is required for change detection modes and forbidden for "outage"',
        });
    }
    if (val.status === 'suppressed' && suppressors === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['suppressedBySdtId'],
            message: 'suppressed alerts must reference the suppressing SDT window or migration scope',
        });
    }
    if (suppressors > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['suppressedByScopeId'],
            message: 'an alert is held by an SDT window or a migration scope, never both',
        });
    }
    if (val.status !== 'suppressed' && suppressors > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['suppressedBySdtId'],
            message: 'suppressedBySdtId / suppressedByScopeId are only allowed when status is "suppressed"',
        });
    }
    // Escalation progress must be coherent with alert status:
    //  - a routed OPEN alert is always progressing: either a stage has
    //    delivered (stageIndex >= 0) or stage 0 is scheduled
    //    (nextEscalationAt set) — "routed but going nowhere" is invalid;
    //  - escalation only runs on open alerts — any other status must not
    //    carry a pending nextEscalationAt.
    if (val.status === 'open' &&
        val.escalation.chainId !== null &&
        val.escalation.stageIndex === -1 &&
        val.escalation.nextEscalationAt === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['escalation'],
            message: 'a routed open alert must have a delivered stage or a scheduled next escalation',
        });
    }
    if (val.status !== 'open' && val.escalation.nextEscalationAt !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['escalation', 'nextEscalationAt'],
            message: 'escalation is halted on non-open alerts (nextEscalationAt must be null)',
        });
    }
});
/* --------------------- API write inputs --------------------- */
export const alertAckInputSchema = z
    .object({
    note: z.string().max(10000).optional(),
})
    .strict();
export const alertNoteInputSchema = z
    .object({
    note: z.string().min(1).max(10000),
})
    .strict();
export const alertResolveInputSchema = z
    .object({
    note: z.string().max(10000).optional(),
})
    .strict();
//# sourceMappingURL=alert.schema.js.map